import * as React from 'react';
import { Combobox } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/24/solid';
import { Checkbox } from 'components/tailwind-ui/Checkbox';
import { Badge } from './Badge';

interface Props {
  label: string;
  placeholder: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any[];
  uniqueKey: string;
  displayKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValue: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (values: any[]) => void;
}

export const ComboboxMultiselect: React.FC<Props> = (props) => {
  const { label, placeholder, options, uniqueKey, displayKey, defaultValue, onChange } = props;

  const [query, setQuery] = React.useState('');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedOptions, setSelectedOptions] = React.useState(defaultValue);
  const [focused, setFocused] = React.useState(false);

  const filteredOptions =
    query === ''
      ? options
      : options.filter((o) => {
          return o[displayKey].toLowerCase().includes(query.toLowerCase());
        });

  const handleSelection = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (newOptions: any[]) => {
      setSelectedOptions(newOptions);
      setQuery('');
      if (onChange) {
        onChange(newOptions);
      }
    },
    [onChange]
  );

  return (
    <Combobox as="div" value={selectedOptions}>
      <Combobox.Label className="block text-sm font-medium leading-6 text-gray-900">
        {label}
      </Combobox.Label>
      <div className="relative mt-2">
        <Combobox.Button
          className={`w-full flex flex-wrap rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 placeholder:text-gray-400 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6 ${
            focused && 'ring-2 ring-inset ring-blue-600'
          }`}
        >
          {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            selectedOptions.map((option: any) => (
              <Badge
                key={option[uniqueKey]}
                label={option[displayKey]}
                showX
                onXClick={(e) => {
                  e.stopPropagation();
                  const newSelectedOptions = selectedOptions.filter(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (o: any) => o[uniqueKey] !== option[uniqueKey]
                  );
                  handleSelection(newSelectedOptions);
                }}
              />
            ))
          }
          <Combobox.Input
            placeholder={placeholder}
            onChange={(event) => setQuery(event.target.value)}
            className={`bg-transparent h-6 border-none focus:outline-none grow ${
              selectedOptions.length > 0 && 'ml-2'
            }`}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            style={{
              boxShadow: 'unset',
            }}
          />

          <div className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
        </Combobox.Button>

        {filteredOptions.length > 0 && (
          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              filteredOptions.map((option: any) => {
                const checked = selectedOptions
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  .map((o: any) => o[uniqueKey])
                  .includes(option[uniqueKey]);
                return (
                  <Combobox.Option
                    key={option[uniqueKey]}
                    value={option}
                    className={({ active }) =>
                      `relative cursor-default select-none p-0 ${
                        active ? 'bg-blue-400 text-white' : 'text-gray-900'
                      }`
                    }
                    onClick={() => {
                      if (!checked) {
                        handleSelection([...selectedOptions, option]);
                      } else {
                        handleSelection(
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          selectedOptions.filter((o: any) => o[uniqueKey] !== option[uniqueKey])
                        );
                      }
                    }}
                  >
                    <Checkbox
                      id={`${label}-${option[uniqueKey]}`}
                      label={option[displayKey]}
                      className="py-2 pl-8 pr-4"
                      checked={checked}
                      onCheck={() => {}}
                    />
                  </Combobox.Option>
                );
              })
            }
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
};
ComboboxMultiselect.displayName = 'Filters.More.ComboboxMultiselect';
